import * as React from "react";

import { cn } from "lib/utils";

import { Dialog, DialogContent } from "./dialog";

const CLOSE_DURATION_MS = 220;

interface AnimatedDialogProps extends React.ComponentPropsWithoutRef<typeof DialogContent> {
  onClose: () => void;
  closeOnEsc?: boolean;
  closeOnOutside?: boolean;
}

export function AnimatedDialog({
  onClose,
  closeOnEsc = true,
  closeOnOutside = true,
  closeDisabled = false,
  className,
  children,
  onEscapeKeyDown,
  onInteractOutside,
  ...props
}: AnimatedDialogProps) {
  const [isClosing, setIsClosing] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(false);
  const closeTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const openFrameRef = React.useRef<number | null>(null);

  const handleClose = React.useCallback(() => {
    if (isClosing || closeDisabled) {
      return;
    }

    setIsClosing(true);
    setIsVisible(false);
    closeTimerRef.current = setTimeout(() => {
      onClose();
      closeTimerRef.current = null;
    }, CLOSE_DURATION_MS);
  }, [closeDisabled, isClosing, onClose]);

  React.useEffect(() => {
    openFrameRef.current = requestAnimationFrame(() => {
      setIsVisible(true);
    });

    return () => {
      if (openFrameRef.current !== null) {
        cancelAnimationFrame(openFrameRef.current);
        openFrameRef.current = null;
      }
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, []);

  return (
    <Dialog
      open
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          handleClose();
        }
      }}
    >
      <DialogContent
        closeDisabled={closeDisabled}
        className={cn(
          "data-[state=open]:animate-none data-[state=closed]:animate-none transition-opacity ease-out",
          isClosing ? "duration-200" : "duration-300",
          isVisible && !isClosing ? "opacity-100" : "opacity-0",
          isClosing ? "pointer-events-none" : "",
          className,
        )}
        onEscapeKeyDown={(event) => {
          if (closeDisabled || !closeOnEsc) {
            event.preventDefault();
            onEscapeKeyDown?.(event);
            return;
          }

          event.preventDefault();
          handleClose();
          onEscapeKeyDown?.(event);
        }}
        onInteractOutside={(event) => {
          if (closeDisabled || !closeOnOutside) {
            event.preventDefault();
          }

          onInteractOutside?.(event);
        }}
        {...props}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
}
